// src/admin/components/CrudTable.jsx
import React from 'react';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Responsive CRUD table.
 * - Desktop/tablet: classic table (unchanged).
 * - Mobile (≤ sm): renders a stacked "card list" per row for readability and larger hit targets.
 */
export default function CrudTable({
                                      ariaLabel,
                                      columns = [],
                                      rows = [],
                                      onRowClick,
                                      onEdit,
                                      onDelete,
                                      height = 'calc(100dvh - 260px)' // dynamic viewport for mobile
                                  }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hasActions = Boolean(onEdit || onDelete);

    if (isMobile) {
        // Card list view for phones
        return (
            <Box
                component={Paper}
                role="region"
                aria-label={ariaLabel}
                sx={{ maxHeight: height, overflow: 'auto' }}
            >
                {rows.length === 0 && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>No records</Box>
                )}

                <Stack spacing={1} sx={{ pb: 1 }}>
                    {rows.map((r, idx) => {
                        const key = r.id ?? idx;
                        const [firstCol, ...restCols] = columns;
                        const firstContent = firstCol
                            ? (firstCol.render ? firstCol.render(r[firstCol.key], r) : (r[firstCol.key] ?? '—'))
                            : null;

                        return (
                            <Paper
                                key={key}
                                variant="outlined"
                                sx={{ mx: 1, mt: 1, p: 1.25, borderRadius: 1 }}
                            >
                                {/* Header row with title + actions */}
                                <Box
                                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: onRowClick ? 'pointer' : 'default'
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, pr: 1 }}>
                                        {firstContent}
                                    </Typography>

                                    {hasActions && (
                                        <Box onClick={(e) => e.stopPropagation()}>
                                            {onEdit && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onEdit(r)}
                                                        aria-label="Edit"
                                                    >
                                                        <EditIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {onDelete && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => onDelete(r)}
                                                        aria-label="Delete"
                                                    >
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {restCols.length > 0 && <Divider sx={{ my: 1 }} />}

                                {/* Details: label above value (one per row) */}
                                <Box sx={{ display: 'grid', rowGap: 0.75 }}>
                                    {restCols.map((c) => {
                                        const value = r[c.key];
                                        const content = c.render ? c.render(value, r) : (value ?? '—');
                                        return (
                                            <Box
                                                key={c.key}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '120px 1fr',
                                                    columnGap: 1
                                                }}
                                            >
                                                <Typography variant="overline" sx={{ lineHeight: 1 }}>
                                                    {c.header || c.key}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ wordBreak: 'break-word' }}
                                                >
                                                    {content}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Paper>
                        );
                    })}
                </Stack>
            </Box>
        );
    }

    // Desktop/tablet table view (unchanged behavior)
    return (
        <TableContainer component={Paper} sx={{ maxHeight: height, overflow: 'auto' }}>
            <Table size="small" stickyHeader aria-label={ariaLabel}>
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell
                                key={c.key}
                                sx={{ whiteSpace: c.nowrap ? 'nowrap' : 'normal', width: c.width }}
                            >
                                {c.header || c.key}
                            </TableCell>
                        ))}
                        {hasActions && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, idx) => (
                        <TableRow
                            key={r.id ?? idx}
                            hover
                            onClick={onRowClick ? () => onRowClick(r) : undefined}
                            sx={{
                                cursor: onRowClick ? 'pointer' : 'default',
                                '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } // striped
                            }}
                        >
                            {columns.map((c) => {
                                const value = r[c.key];
                                const content = c.render ? c.render(value, r) : (value ?? '—');
                                return (
                                    <TableCell
                                        key={c.key}
                                        sx={{ whiteSpace: c.nowrap ? 'nowrap' : 'normal' }}
                                    >
                                        {content}
                                    </TableCell>
                                );
                            })}
                            {hasActions && (
                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    {onEdit && (
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => onEdit(r)} aria-label="Edit">
                                                <EditIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {onDelete && (
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => onDelete(r)} aria-label="Delete">
                                                <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell align="center" colSpan={columns.length + (hasActions ? 1 : 0)}>
                                No records
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
